import React from 'react';
import { Helmet } from 'react-helmet';
import { books } from '../../data/books';
import { BASE_URL } from '../../data/constants';

export const SEOHead = ({ selectedBook, sales }) => {
  // Função para gerar Schema.org JSON-LD
  const generateStructuredData = () => {
    const websiteSchema = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "EbookArena",
      "alternateName": "Ebook Arena",
      "description": "A arena dos ebooks políticos do Brasil. Análises profundas sobre esquerda e direita com ebooks exclusivos e dados reais da política brasileira.",
      "url": BASE_URL,
      "potentialAction": {
        "@type": "SearchAction",
        "target": {
          "@type": "EntryPoint",
          "urlTemplate": `${BASE_URL}/busca?q={search_term_string}`
        },
        "query-input": "required name=search_term_string"
      },
      "publisher": {
        "@type": "Organization",
        "@id": `${BASE_URL}#organization`
      }
    };

    const organizationSchema = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "@id": `${BASE_URL}#organization`,
      "name": "EbookArena",
      "description": "Plataforma educativa dedicada à análise política brasileira",
      "url": BASE_URL,
      "logo": {
        "@type": "ImageObject",
        "url": `${BASE_URL}/logo-ebokarena.png`,
        "width": 600,
        "height": 200
      },
      "sameAs": [
        "https://twitter.com/ebokarena",
        "https://facebook.com/ebokarena",
        "https://instagram.com/ebokarena"
      ],
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "Customer Service",
        "email": "ebookarena2026@gmail.com",
        "availableLanguage": "Portuguese"
      }
    };

    const productSchemas = Object.entries(books).map(([side, book]) => ({
      "@context": "https://schema.org",
      "@type": "Book",
      "@id": `${BASE_URL}/ebook-${side}`,
      "name": book.title,
      "alternateName": book.subtitle,
      "description": book.description,
      "isbn": book.isbn,
      "bookFormat": "https://schema.org/EBook",
      "inLanguage": "pt-BR",
      "numberOfPages": parseInt(book.pages.split(' ')[0]),
      "datePublished": book.publishDate,
      "publisher": {
        "@type": "Organization",
        "name": "EbookArena"
      },
      "author": {
        "@type": "Person",
        "name": book.author
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": book.rating,
        "ratingCount": sales[side] + 50,
        "bestRating": "5",
        "worstRating": "1"
      },
      "offers": {
        "@type": "Offer",
        "price": book.priceValue,
        "priceCurrency": "BRL",
        "availability": "https://schema.org/InStock",
        "validFrom": "2025-01-01",
        "priceValidUntil": "2025-12-31",
        "seller": {
          "@type": "Organization",
          "@id": `${BASE_URL}#organization`
        }
      },
      "keywords": book.keywords,
      "genre": "Political Analysis",
      "about": [
        {
          "@type": "Thing",
          "name": "Política Brasileira"
        },
        {
          "@type": "Thing", 
          "name": side === 'esquerda' ? "Progressismo" : "Conservadorismo"
        }
      ]
    }));

    return [websiteSchema, organizationSchema, ...productSchemas];
  };

  // SEO Meta Data
  const seoData = {
    title: selectedBook 
      ? `${books[selectedBook].title} - Ebook Político | EbookArena`
      : "Batalha dos Ebooks Políticos | Esquerda vs Direita | EbookArena",
    description: selectedBook
      ? `${books[selectedBook].description} Baixe agora por apenas R$ 9,90 no EbookArena.`
      : "Descubra os bastidores da política brasileira! Duas perspectivas, uma escolha. Análises exclusivas sobre esquerda e direita com dados reais. Escolha seu lado na batalha política!",
    keywords: selectedBook
      ? `${books[selectedBook].keywords}, ebook político, EbookArena`
      : "política brasil, ebook político, esquerda vs direita, análise política brasileira, progressismo, conservadorismo, EbookArena",
    canonicalUrl: selectedBook
      ? `${BASE_URL}/ebook-${selectedBook}`
      : BASE_URL,
    ogImage: `${process.env.REACT_APP_FRONT_URL}/ogimage_1.jpg`
  };

  return (
    <Helmet>
      <html lang="pt-BR" />
      <title>{seoData.title}</title>
      <meta name="description" content={seoData.description} />
      <meta name="keywords" content={seoData.keywords} />
      <link rel="canonical" href={seoData.canonicalUrl} />
      
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="bingbot" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      
      <meta name="geo.region" content="BR" />
      <meta name="geo.country" content="Brazil" />
      <meta name="language" content="Portuguese" />
      <meta name="distribution" content="global" />
      
      <meta property="og:title" content={seoData.title} />
      <meta property="og:description" content={seoData.description} />
      <meta property="og:image" content={seoData.ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:url" content={seoData.canonicalUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="EbookArena" />
      <meta property="og:locale" content="pt_BR" />
      
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seoData.title} />
      <meta name="twitter:description" content={seoData.description} />
      <meta name="twitter:image" content={seoData.ogImage} />
      <meta name="twitter:site" content="@ebokarena" />
      <meta name="twitter:creator" content="@ebokarena" />
      
      <meta name="viewport" content="width=device-width, initial-scale=1, shrink-to-fit=no" />
      <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
      <meta name="format-detection" content="telephone=no" />
      
      <meta name="application-name" content="EbookArena" />
      <meta name="apple-mobile-web-app-title" content="EbookArena" />
      <meta name="msapplication-TileColor" content="#2563eb" />
      <meta name="theme-color" content="#2563eb" />
      
      {generateStructuredData().map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
      
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link rel="preconnect" href="https://app.sidemap.net" />
      
      <link rel="dns-prefetch" href="//google-analytics.com" />
      <link rel="dns-prefetch" href="//googletagmanager.com" />
      
      <meta name="product" content="Ebooks Políticos Brasileiros" />
      <meta name="price" content="29.90" />
      <meta name="priceCurrency" content="BRL" />
      <meta name="availability" content="InStock" />
      
      <meta name="author" content="EbookArena" />
      <meta name="publisher" content="EbookArena" />
      <meta name="copyright" content="© 2025 EbookArena. Todos os direitos reservados." />
      
      <link rel="alternate" hrefLang="pt-BR" href={BASE_URL} />
      <link rel="alternate" hrefLang="x-default" href={BASE_URL} />
    </Helmet>
  );
};